import { computed, unref, type Ref } from 'vue';
import { dinero, toDecimal } from 'dinero.js';
import { AUD } from 'dinero.js/currencies';
import { useSessionStore, useAppStore } from '@/store';
import { useCustomerPricingSource } from '@/composables/useCustomerPricingSource';
import { priceUnitName, priceUnitSuffix } from '@/composables/usePriceUnitFormatting';

type OrderItemId = number | string;

export function usePriceBreakdown(options?: { orderItemIds?: Ref<OrderItemId[]> | OrderItemId[] }) {
    const session = useSessionStore();
    const appStore = useAppStore();
    const pricingSource = useCustomerPricingSource();

    const orderItemIds = computed<OrderItemId[] | undefined>(() => {
        return (options && options.orderItemIds ? unref(options.orderItemIds) : undefined) as OrderItemId[] | undefined;
    });

    const parseMoneyStringToCents = (formatted: string | null | undefined): number => {
        if (!formatted) return 0;
        const cleaned = formatted.replace(/[$,\s]/g, '');
        const value = parseFloat(cleaned);
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
    };

    const breakdownItems = computed(() => {
        const items: Array<{
            label: string;
            unitCost: string | null;
            rows: Array<{ label: string; value: string; muted?: boolean }>;
        }> = [];

        const orderItems = (session.order as any)?.items ?? [];

        let confirmableItems = orderItems.filter(
            (item: any) => item?.status !== 'CONFIRMED' || item?.service_status === 'Pending',
        );

        const ids = unref(orderItemIds);
        if (ids && ids.length) {
            const idSet = new Set(ids);
            confirmableItems = confirmableItems.filter((i: any) => idSet.has(i.id));
        }

        // Exclude storage when any non-storage items are present
        const hasNonStorage = confirmableItems.some(
            (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
        );
        if (hasNonStorage) {
            confirmableItems = confirmableItems.filter(
                (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
            );
        }

        // Helpers
        const formatDate = (iso: string | null | undefined, tz?: string | null): string => {
            if (!iso) return '-';
            try {
                const date = new Date(iso);
                const formatter = new Intl.DateTimeFormat('en-AU', {
                    timeZone: tz ?? undefined,
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
                return formatter.format(date);
            } catch {
                return '-';
            }
        };

        const deriveUnit = (product: any): string | null => {
            return priceUnitName(product?.price_unit_formatted);
        };

        confirmableItems.forEach((orderItem: any) => {
            const label = orderItem.service_label ?? orderItem.service_type ?? 'Service';
            let unitCost: string | null = null;
            let isVariable: boolean;
            let isMove = false;

            // STORAGE handling
            const isStorage = ((orderItem.productable_type ?? '') as string).toLowerCase() === 'storage';
            if (isStorage) {
                const product = orderItem.product ?? {};
                const unit = deriveUnit(product);
                const storagePrice = product?.price?.formatted;
                if (storagePrice && unit) {
                    unitCost = `${storagePrice} / ${unit}`;
                } else if (storagePrice) {
                    unitCost = storagePrice;
                }

                const start = formatDate(product?.start_date, product?.timezone?.timezone);
                const end = formatDate(product?.end_date, product?.timezone?.timezone);

                items.push({
                    label,
                    unitCost,
                    rows: [
                        { label: 'Start date', value: start },
                        { label: 'End date', value: end },
                    ],
                });
                return; // proceed next item
            }

            if ((orderItem.productable_type ?? '').toLowerCase() === 'move') {
                const selectedMatch = session.getSelectedMatch;
                // Pricing fields resolve from the booked job once one is active, so
                // post-booking operator edits reach the customer; match pre-booking.
                const pricingType = pricingSource.pricingType.value;
                isVariable = pricingType === 'VARIABLE';
                isMove = true;

                if (session.isLocal && isVariable && selectedMatch?.price_per_unit?.formatted) {
                    if (selectedMatch.price_per_hour) {
                        unitCost = `${selectedMatch.price_per_hour.formatted} / hr`;
                    } else {
                        unitCost = `${selectedMatch.price_per_unit.formatted} / 30 mins`;
                    }
                } else if (!isVariable && orderItem.totals?.TOTAL?.formatted) {
                    // FIXED moves show a single all-in "Move" price: every line (itemised visible
                    // lines and hidden "Other fees") is folded in, so it equals the order total.
                    unitCost = orderItem.totals.TOTAL.formatted;
                }
            } else {
                const product = orderItem.product ?? {};
                const rawBasePrice: string | null =
                    product?.price?.formatted ?? orderItem.totals?.TOTAL?.formatted ?? null;
                const basePrice: string | null = rawBasePrice;
                const priceUnit: string | null = product?.price_unit ?? null;
                const priceUnitFormatted: any = product?.price_unit_formatted ?? null;
                // Backend exposes a doubled `price_per_hour` for variable per-30-min
                // services when the tenant's per-hour pricing setting is on. Display only.
                const pricePerHour: string | null = product?.price_per_hour?.formatted ?? null;

                // Determine variable status for non-move items. `isVariable` only decides whether
                // the balance is still unknown, so it has to fall away once the service completes
                // — by then the final quantity has been captured and the balance is real. Keying
                // it on price_type alone left "To be calculated after service complete" on screen
                // permanently, with the actual amount hidden behind it.
                const productPriceType: string | null = product?.price_type ?? null;
                const depositType: string | null = product?.deposit_type ?? null;
                const quantity: number | null = product?.quantity ?? null;
                const isComplete = String(orderItem.service_status ?? '').toUpperCase() === 'COMPLETED';
                isVariable =
                    !isComplete && (productPriceType === 'VARIABLE' || depositType === 'METERED' || !!quantity);

                if (pricePerHour) {
                    unitCost = `${pricePerHour} / hour`;
                } else if (basePrice) {
                    const suffix = priceUnitSuffix(priceUnit ?? undefined, priceUnitFormatted);
                    unitCost = suffix ? `${basePrice} ${suffix}` : basePrice;
                } else {
                    unitCost = null;
                }
            }

            // BALANCE is `amount_total - deposit` with no floor, so a service that finishes under
            // the deposited quantity produces a negative. The customer is still charged
            // max(total, deposit) — see PackingProductableTrait::getAmountOwingAttribute — so
            // nothing is actually owed, and surfacing a negative here would read as a refund.
            const balanceAmount = Number(orderItem.totals?.BALANCE?.amount ?? 0);
            const remaining: string | null =
                isVariable ? null
                : orderItem.totals?.BALANCE?.formatted && balanceAmount >= 0 ? orderItem.totals.BALANCE.formatted
                : null;
            // For interstate moves not within a week, show note next to label
            const remainingLabelNote: string | null =
                isMove && !session.isLocal && session.getSelectedMatch?.within_week != 1 ?
                    (() => {
                        const days = appStore.tenantFinalPaymentDays;
                        if (days > 0) return ` (${days} days before move)`;
                        if (days < 0) return ` (${Math.abs(days)} days after move)`;
                        return ' (on move day)';
                    })()
                :   null;
            const remainingNote = isVariable ? 'To be calculated after service complete' : null;

            const rows: Array<{ label: string; value: string; muted?: boolean }> = [];

            // For Move items, add pricing breakdown sections
            if (isMove) {
                const selectedMatch = session.getSelectedMatch;
                const pricingType = pricingSource.pricingType.value;

                // Call-out fee for variable pricing (fee details still read the match —
                // job-level call-out preference is a separate concern)
                if (pricingType === 'VARIABLE') {
                    const callOutFeeAmount = Number(selectedMatch?.call_out_fee?.amount ?? 0);
                    if (callOutFeeAmount > 0) {
                        const callOutFeeValue =
                            appStore.tenantCallOutFeeAsTime && selectedMatch?.call_out_fee_time ?
                                selectedMatch.call_out_fee_time
                            :   selectedMatch?.call_out_fee?.formatted || '-';
                        rows.push({ label: 'Call-out fee', value: callOutFeeValue });
                    }
                }

                // Visible (non-hidden) lines are itemised underneath the "Move" header for both
                // pricing types. On FIXED the header already equals the full total (all values fold
                // in), so these rows are an "of which" detail; the hidden fees are absorbed into the
                // header and never itemised (only VARIABLE shows the "Other fees" line below).
                const customerVisiblePrices: Array<{ label: string; value: string }> =
                    pricingSource.customerVisiblePrices.value;
                for (const price of customerVisiblePrices) {
                    rows.push({ label: price.label, value: price.value });
                }

                // Only VARIABLE moves surface "Other fees" as its own line; FIXED folds it into Move above.
                const otherFees = pricingSource.otherFees.value;
                if (pricingType === 'VARIABLE' && Number(otherFees?.amount ?? 0) > 0 && otherFees?.formatted) {
                    rows.push({
                        label: pricingSource.otherFeesLabel.value,
                        value: otherFees.formatted,
                    });
                }

                // Deposit
                rows.push({
                    label: 'Deposit (due now)',
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });

                if (pricingType === 'VARIABLE') {
                    rows.push({
                        label: 'Minimum hours',
                        value: Number(selectedMatch?.min_time ?? appStore.tenantMinHours).toFixed(0) + ' hours',
                    });
                }

                // Final payment (renamed from Remaining)
                if (appStore.tenantAutoCreateFinal) {
                    if (remainingNote) {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: remainingNote, muted: true });
                    } else {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: (remaining ?? '-') as string });
                    }
                }
            } else {
                // For non-Move items, keep existing structure
                // Deposit row
                rows.push({
                    label: 'Deposit (due now)',
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });
                // Remaining row
                if (remainingNote) {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: remainingNote, muted: true });
                } else {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: (remaining ?? '-') as string });
                }
            }

            items.push({
                label,
                unitCost,
                rows,
            });
        });

        return items;
    });

    const displayPricesExTax = computed(
        () => (appStore.tenant as any)?.company?.settings?.display_prices_ex_tax ?? false,
    );

    const dueNowTotal = computed(() => {
        const computeDueNow = (ids?: Array<number | string>) => {
            const orderItems = (session.order as any)?.items ?? [];
            const idSet = ids && ids.length > 0 ? new Set(ids) : null;
            // Sum the same DEPOSIT.formatted values shown in the deposit rows. The backend already
            // applies any ex-tax / region adjustment to `.formatted` (PricingMoneyData), so the total
            // stays consistent with the displayed rows without re-deriving the tax rate here.
            const cents = orderItems
                .filter((item: any) => item?.status !== 'CONFIRMED')
                // Exclude storage from due now
                .filter((item: any) => ((item?.productable_type ?? '') as string).toLowerCase() !== 'storage')
                .filter((item: any) => (idSet ? idSet.has(item.id) : true))
                .reduce((acc: number, item: any) => acc + parseMoneyStringToCents(item?.totals?.DEPOSIT?.formatted), 0);

            return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
                parseFloat(toDecimal(dinero({ amount: cents, currency: AUD }))),
            );
        };

        const ids = orderItemIds.value as Array<number | string> | undefined;
        return computeDueNow(ids);
    });

    return {
        breakdownItems,
        dueNowTotal,
        displayPricesExTax,
    };
}
